/* eslint-disable no-unused-vars */
import loaderSignal from 'signals/Loader.signal';
import userAccountSignal from 'signals/UserAccount.signal';
import {
  businessSettingsAddressSearchSignal,
  businessSettingsDirtySignal,
  businessSettingsErrorsSignal,
  businessSettingsIndustryOptionsSignal,
  businessSettingsSignal,
  businessSettingsSubmittingSignal,
} from 'signals/BusinessSettings.signal';
import alertSignal from 'signals/Alert.signal';
import api from 'api/api';
import history from '../../../../history';
import { expandFlatObject, flattenNestedObject } from 'utils/objects';
import {
  einRegex,
  naicsCodeRegex,
} from '../../../global/Constant/regularExpression';
import { INDUSTRY_LIST } from 'components/global/Constant/constants';
import queryAddresses from 'utils/queryAddresses';
import validateUrl from 'utils/validateUrl';
import validateEmail from 'utils/validateEmail';
import { validatePhoneNumber } from 'utils/validatePhoneNumber';

export function handleTabChange(tab) {
  fetchAndSetBusiness();
  businessSettingsDirtySignal.reset();
  return history.replace(`/business/organization?tab=${tab}`);
}

/**
 * Single-line address for the Bing-style autocomplete when `address.formattedAddress`
 * was never set (common for imports / manual DB rows with only addressLine1, city, …).
 */
export function getBusinessAddressAutocompleteValue(form) {
  if (!form || typeof form !== 'object') return '';
  const fromNested =
    form.address &&
    typeof form.address === 'object' &&
    form.address.formattedAddress;
  const fromTop = form.formattedAddress;
  const fa = fromNested || fromTop;
  if (fa != null && String(fa).trim() !== '') return String(fa).trim();

  const line1 = form.addressLine1 != null ? String(form.addressLine1).trim() : '';
  const line2 = form.addressLine2 != null ? String(form.addressLine2).trim() : '';
  const city = form.city != null ? String(form.city).trim() : '';
  const state = form.state != null ? String(form.state).trim() : '';
  const zip = form.zipCode != null ? String(form.zipCode).trim() : '';
  const county = form.county != null ? String(form.county).trim() : '';
  const cityState = [city, state].filter(Boolean).join(', ');
  const parts = [line1, line2, cityState, zip, county].filter(Boolean);
  if (parts.length) return parts.join(', ');

  const phys = form.physicalAddress != null ? String(form.physicalAddress).trim() : '';
  return phys || '';
}

const syncBusinessSignals = (business) => {
  const flattenedBusiness = flattenNestedObject(business);
  const selectedIndustries =
    flattenedBusiness['assistance.industrySelection'] || [];
  const industryOptions = [
    ...new Set(INDUSTRY_LIST.concat(selectedIndustries)),
  ];
  businessSettingsIndustryOptionsSignal.update({
    industries: industryOptions,
  });
  const addressLine = getBusinessAddressAutocompleteValue(flattenedBusiness);
  businessSettingsAddressSearchSignal.update({
    addressSearch: addressLine,
    addressOptions: [],
  });
  return businessSettingsSignal.update(flattenedBusiness);
};

export const fetchAndSetBusiness = async () => {
  loaderSignal.update({ isContentLoading: true });
  try {
    const business = await api.get({
      path: '/businesses',
    });
    return syncBusinessSignals(business);
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const fetchAndSetBusinessById = async (businessId) => {
  loaderSignal.update({ isContentLoading: true });
  try {
    const business = await api.get({
      path: `/v2/businesses/${businessId}`,
    });
    return syncBusinessSignals(business);
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

const NON_EMPTY_KEYS = [
  'name',
  'formOfBusinessOrganization',
  'description',
  'address',
];

const NUMERIC_KEYS = [
  'assistance.seekingMin',
  'assistance.seekingMax',
  'assistance.downPayment',
  'employment.numberOfEmployees',
  'employment.numberOfFullTimeEmployees',
  'employment.numberOfPartTimeEmployees',
  'employment.expectedJobsRetained',
  'employment.expectedJobsCreated',
  'yearBusinessOpened',
  'finance.annualRevenue',
  'finance.previousAnnualRevenue',
  'finance.netIncome',
  'finance.allocatedFundsExpendedOrSetAsideForGuarantysOrCollateralSupportObligations',
  'finance.recycledFundsExpendedOrSetAsideForGuarantysOrCollateralSupportObligations',
  'finance.additionalGovernmentFunds',
  'finance.concurrentPrivateFinancing',
];

const CHECKBOX_KEYS = [
  'demographics.race.americanIndianOrAlaskaNative',
  'demographics.race.blackOrAfricanAmerican',
  'demographics.race.asianIndian',
  'demographics.race.asianChinese',
  'demographics.race.asianFilipino',
  'demographics.race.asianJapanese',
  'demographics.race.asianKorean',
  'demographics.race.asianVietnamese',
  'demographics.race.asianOther',
  'demographics.race.guamanianOrChamorro',
  'demographics.race.nativeHawaiian',
  'demographics.race.samoan',
  'demographics.race.pacificIslanderOther',
  'demographics.race.white',
];

const MULTISELECT_KEYS = [
  'assistance.desiredAssistanceTypes',
  'assistance.assistanceUses',
  'assistance.industrySelection',
];

const maskPhoneNumber = (rawValue = '') => {
  const digits = String(rawValue).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Keep only persisted Bing-style fields on Business.address Json (omit geocodePoints, etc.).
 */
function normalizeBusinessAddressForApi(address) {
  if (!address || typeof address !== 'object') return null;
  const {
    countryRegion = '',
    locality = '',
    adminDistrict = '',
    adminDistrict2 = '',
    postalCode = '',
    addressLine = '',
    formattedAddress = '',
  } = address;
  return {
    countryRegion,
    locality,
    adminDistrict,
    adminDistrict2,
    postalCode,
    addressLine,
    formattedAddress,
  };
}

/** If dirty payload includes `address`, require a chosen autocomplete row — not `{}` mid-search. */
function validateAndNormalizeDirtyAddress(payload) {
  if (!Object.prototype.hasOwnProperty.call(payload, 'address')) return;

  const normalized = normalizeBusinessAddressForApi(payload.address);
  const fa = normalized && String(normalized.formattedAddress).trim();

  if (!fa) {
    throw new Error(
      'Please select a valid address from the drop down.'
    );
  }

  payload.address = normalized;
}

/**
 * Legacy Business columns drive search/UI fallbacks when Json address alone is stale.
 */
function denormalizeAddressColumnsIntoPayload(payload) {
  const a = payload.address;
  if (!a || typeof a !== 'object') return;

  payload.addressLine1 = a.addressLine ?? '';
  payload.city = a.locality ?? '';
  payload.state = a.adminDistrict ?? '';
  payload.zipCode = a.postalCode ?? '';
  if (a.adminDistrict2 != null && String(a.adminDistrict2).trim() !== '') {
    payload.county = a.adminDistrict2;
  }
}

const getBusinessSaveErrorMessage = (error) => {
  if (error?.status === 409) {
    return 'A business with this EIN already exists.';
  }
  return error?.message || 'Unable to update business information.';
};

export function handleBusinessFormChange(e) {
  const { name, value } = e.target;
  const inputValue = name === 'phoneNumber' ? maskPhoneNumber(value) : value;

  handleBusinessFormDirtyChange({ target: { name } });

  if (NON_EMPTY_KEYS.includes(name)) {
    businessSettingsErrorsSignal.update({ [name]: !inputValue });
  }

  if (name === 'assistance.seekingMin') {
    businessSettingsErrorsSignal.update({ [name]: !parseInt(inputValue, 10) });
    const seekingMax = businessSettingsSignal.value['assistance.seekingMax'];
    const invalid =
      seekingMax && parseInt(inputValue, 10) >= parseInt(seekingMax, 10);
    businessSettingsErrorsSignal.update({ ['assistance.seekingMax']: invalid });
  } else if (name === 'assistance.seekingMax') {
    const seekingMin = businessSettingsSignal.value['assistance.seekingMin'];
    const invalid =
      !parseInt(seekingMin, 10) ||
      !parseInt(inputValue, 10) ||
      parseInt(seekingMin, 10) >= parseInt(inputValue, 10);
    businessSettingsErrorsSignal.update({ [name]: invalid });
  } else if (NUMERIC_KEYS.includes(name)) {
    businessSettingsErrorsSignal.update({ [name]: !parseInt(inputValue, 10) });
  }

  if (name === 'ein') {
    businessSettingsErrorsSignal.update({ [name]: !einRegex.test(inputValue) });
  }

  if (name === 'naicsCode') {
    businessSettingsErrorsSignal.update({
      [name]: !naicsCodeRegex.test(inputValue),
    });
  }

  if (name === 'website') {
    if (inputValue) {
      businessSettingsErrorsSignal.update({ [name]: !validateUrl(inputValue) });
    } else {
      businessSettingsErrorsSignal.update({ [name]: false }); // website is not required
    }
  }

  if (name === 'contactEmail') {
    businessSettingsErrorsSignal.update({
      [name]: inputValue ? !validateEmail(inputValue) : false,
    });
  }

  if (name === 'phoneNumber') {
    businessSettingsErrorsSignal.update({
      [name]: inputValue ? !validatePhoneNumber(inputValue) : false,
    });
  }

  const checkBoxKey = CHECKBOX_KEYS.find((key) => name.startsWith(key));
  const multiselectKey = MULTISELECT_KEYS.find((key) => name.startsWith(key));

  if (name === 'demographics.race.preferNotToRespond') {
    const value = businessSettingsSignal.value[name];
    if (!value) {
      const currentValue = businessSettingsSignal.value;
      Object.keys(currentValue).forEach((key) => {
        if (key.startsWith('demographics.race')) {
          delete currentValue[key];
        }
      });
    }

    businessSettingsSignal.update({
      [name]: !value,
    });
  } else if (checkBoxKey) {
    businessSettingsSignal.update({
      [checkBoxKey]: !businessSettingsSignal.value[checkBoxKey],
      'demographics.race.preferNotToRespond': false,
    });
  } else if (multiselectKey) {
    const options = businessSettingsSignal.value[multiselectKey] || [];
    const newValue = options.includes(value)
      ? {
        [multiselectKey]: options.filter((v) => v !== value),
      }
      : {
        [multiselectKey]: (options || []).concat(value),
      };

    businessSettingsSignal.update(newValue);
  } else {
    return businessSettingsSignal.update({ [name]: inputValue });
  }
}

export function handleBusinessFormDirtyChange(e) {
  const { name } = e.target;
  const key = MULTISELECT_KEYS.find((key) => name.startsWith(key));
  if (key) {
    return businessSettingsDirtySignal.update({ [key]: true });
  }
  return businessSettingsDirtySignal.update({ [name]: true });
}

export function handleNewIndustryChange(e) {
  return businessSettingsIndustryOptionsSignal.update({
    value: e.target.value,
  });
}

export function handleAddAnotherIndustry(newIndustry) {
  const { industries } = businessSettingsIndustryOptionsSignal.value;
  if (newIndustry === '') {
    return alertSignal.update({
      type: 'notification',
      error: {},
      message: 'Industry cannot be empty.',
    });
  } else if (industries.includes(newIndustry)) {
    return alertSignal.update({
      type: 'notification',
      error: {},
      message: 'Industry already exists.',
    });
  } else {
    return businessSettingsIndustryOptionsSignal.update({
      industries: [...industries, ...[newIndustry]],
      value: '',
    });
  }
}

export async function submitBusiness(e) {
  e.preventDefault();

  try {
    loaderSignal.update({ isContentLoading: true, message: 'Updating...' });
    const allDirtyPairs = Object.entries(
      businessSettingsDirtySignal.value
    ).filter(([, value]) => value);

    if (Object.keys(allDirtyPairs).length === 0) {
      return alertSignal.update({
        // client requested to always say successfully saved if no errors present
        variant: 'success',
        type: 'notification',
        message: 'Successfully updated business information.',
      });
    }

    const errorsObject = businessSettingsErrorsSignal.value;
    if (allDirtyPairs.some(([key]) => errorsObject[key])) {
      throw new Error('Cannot submit if there are any errors present.');
    }

    const payload = allDirtyPairs.reduce(
      (acc, [key]) => ({
        ...acc,
        [key]: NUMERIC_KEYS.includes(key)
          ? parseInt(businessSettingsSignal.value[key], 10)
          : businessSettingsSignal.value[key],
      }),
      {}
    );

    validateAndNormalizeDirtyAddress(payload);
    if (payload.address) {
      denormalizeAddressColumnsIntoPayload(payload);
    }

    businessSettingsSubmittingSignal.update(true);

    await api.patch({
      path: '/businesses',
      body: {
        where: {
          id: userAccountSignal.value.userData.account.business.id,
        },
        data: expandFlatObject(payload),
      },
    });
    await fetchAndSetBusiness();
    businessSettingsDirtySignal.reset();
    alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated business information.',
    });
  } catch (error) {
    alertSignal.update({
      variant: 'danger',
      type: 'notification',
      error,
      message: getBusinessSaveErrorMessage(error),
    });
  } finally {
    businessSettingsSubmittingSignal.update(false);
    loaderSignal.update({ isContentLoading: false });
  }
}

export async function submitBusinessForEdo(e, businessId, onSuccess) {
  e.preventDefault();

  try {
    loaderSignal.update({ isContentLoading: true, message: 'Updating...' });
    const allDirtyPairs = Object.entries(
      businessSettingsDirtySignal.value
    ).filter(([, value]) => value);

    if (Object.keys(allDirtyPairs).length === 0) {
      if (onSuccess) {
        await onSuccess();
      }
      return alertSignal.update({
        variant: 'success',
        type: 'notification',
        message: 'Successfully updated business information.',
      });
    }

    const errorsObject = businessSettingsErrorsSignal.value;
    if (allDirtyPairs.some(([key]) => errorsObject[key])) {
      throw new Error('Cannot submit if there are any errors present.');
    }

    const payload = allDirtyPairs.reduce(
      (acc, [key]) => ({
        ...acc,
        [key]: NUMERIC_KEYS.includes(key)
          ? parseInt(businessSettingsSignal.value[key], 10)
          : businessSettingsSignal.value[key],
      }),
      {}
    );

    validateAndNormalizeDirtyAddress(payload);

    businessSettingsSubmittingSignal.update(true);

    await api.patch({
      path: `/v2/businesses/${businessId}`,
      body: expandFlatObject(payload),
    });
    await fetchAndSetBusinessById(businessId);
    businessSettingsDirtySignal.reset();
    alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated business information.',
    });
    if (onSuccess) {
      await onSuccess();
    }
  } catch (error) {
    alertSignal.update({
      variant: 'danger',
      type: 'notification',
      error,
      message: getBusinessSaveErrorMessage(error),
    });
  } finally {
    businessSettingsSubmittingSignal.update(false);
    loaderSignal.update({ isContentLoading: false });
  }
}

export const removeIndustryTag = (value) => {
  businessSettingsIndustryOptionsSignal.update({
    industries: businessSettingsIndustryOptionsSignal.value.industries.filter(
      (industry) => industry !== value
    ),
  });

  if (
    businessSettingsSignal.value['assistance.industrySelection']?.includes(
      value
    )
  ) {
    businessSettingsSignal.update({
      ['assistance.industrySelection']: businessSettingsSignal.value[
        'assistance.industrySelection'
      ].filter((industry) => industry !== value),
    });
  }
};

export const handleAddressSearchChange = (input) => {
  businessSettingsSignal.update({ address: {} });
  businessSettingsAddressSearchSignal.update({ addressSearch: input });
  businessSettingsErrorsSignal.update({ address: false });
};

export const handleSelectAddress = (option) => {
  businessSettingsSignal.update({ address: option });
  businessSettingsAddressSearchSignal.update({
    addressSearch: option.formattedAddress,
  });
  businessSettingsErrorsSignal.update({ address: false });
  businessSettingsDirtySignal.update({ address: true });
};

export const getAddressOptions = async () => {
  const bingAddresses = await queryAddresses(
    businessSettingsAddressSearchSignal.value.addressSearch
  );
  businessSettingsAddressSearchSignal.update({
    addressOptions: bingAddresses.map((ba) => ba),
  });
};

export const handleAddressBlur = () => {
  const form = businessSettingsSignal.value;
  const searchText =
    businessSettingsAddressSearchSignal.value.addressSearch?.trim() || '';
  const hasSelectedAddress = !!Object.keys(form.address || {}).length;
  const composedFromFlat = getBusinessAddressAutocompleteValue(form);
  const textMatchesSavedFlatParts =
    composedFromFlat &&
    searchText &&
    composedFromFlat === searchText;

  if (!searchText) {
    businessSettingsErrorsSignal.update({ address: false });
    return;
  }

  if (!hasSelectedAddress && !textMatchesSavedFlatParts) {
    businessSettingsErrorsSignal.update({ address: true });
  } else {
    businessSettingsErrorsSignal.update({ address: false });
  }
};

export const handleAddressSearchFocus = () => {
  businessSettingsAddressSearchSignal.update({
    addressSearch:
      businessSettingsSignal.value.address?.formattedAddress ||
      businessSettingsAddressSearchSignal.value.addressSearch,
  });
};

export const handleSediDemographicsChange = (e) => {
  const { name } = e;
  const sediDemographics = businessSettingsSignal.value["sediCertification.selfCertifiedSEDIDemographicsRelatedBusinessStatus"] ?? [];

  handleBusinessFormDirtyChange({ target: { name: 'sediCertification.selfCertifiedSEDIDemographicsRelatedBusinessStatus' } })

  if (name === 'N/A' && !sediDemographics.includes('N/A')) {
    businessSettingsSignal.update({
      "sediCertification.selfCertifiedSEDIDemographicsRelatedBusinessStatus": ['N/A'],
    });
  } else {
    businessSettingsSignal.update({
      "sediCertification.selfCertifiedSEDIDemographicsRelatedBusinessStatus": sediDemographics.includes(name) ?
        sediDemographics.filter(sd => sd !== name) :
        [...sediDemographics.filter(sd => sd !== 'N/A'), name],
    });
  }
};
